import { Injectable, inject } from '@angular/core';
import {
  HicapsApi,
  HicapsConnectMethod,
  HicapsConnectResult,
  IPMSHicapsConnectConfig,
} from '@principle-theorem/hicaps-connect';
import { FeatureFlagsService } from '@principle-theorem/ng-feature-flags';
import { BridgeCommandsService } from '@principle-theorem/ng-principle-bridge-cloud';
import {
  HicapsConnectApiAdaptor,
  HicapsConnectProcessHandler,
  HicapsConnectProcessStatus,
  HicapsConnectResponseCodes,
  HicapsConnectTerminal,
  IHicapsConnectApiHooks,
  IHicapsConnectProcess,
  Practice,
} from '@principle-theorem/principle-core';
import {
  IBridgeDevice,
  IHicapsConnectBridgeEdge,
  IHicapsConnectEdge,
  IHicapsConnectMerchant,
  IHicapsConnectTerminalV2,
  IPractice,
} from '@principle-theorem/principle-core/interfaces';
import {
  CollectionReference,
  DocumentReference,
  Firestore,
  IReffable,
  Timestamp,
  WithRef,
  asyncForEach,
  resolveSequentially,
  snapshot,
  undeletedFilter,
} from '@principle-theorem/shared';
import { uniqBy } from 'lodash';
import { NG_PAYMENTS_CONFIG } from '../../../../ng-payments-config';
import { HicapsConnectHelpers } from '../hicaps-connect-helpers';
import { HicapsConnectV2Routing } from './hicaps-connect-v2-routing';

interface IHicapsConnectObservedMerchant {
  merchantId: string;
  providerName: string;
}

interface IHicapsConnectObservedTerminal {
  terminalId: string;
  serverUrl: string;
  isLocal: boolean;
  merchants: IHicapsConnectObservedMerchant[];
  isComplete: boolean;
}

interface IHicapsConnectV2SyncContext {
  terminalCol: CollectionReference<IHicapsConnectTerminalV2>;
  merchantCol: CollectionReference<IHicapsConnectMerchant>;
  terminals: WithRef<IHicapsConnectTerminalV2>[];
}

interface IHicapsConnectBridgeReport {
  terminals: IHicapsConnectObservedTerminal[];
  isComplete: boolean;
}

export interface IHicapsConnectV2SyncProgress {
  completed: number;
  total: number;
}

@Injectable({ providedIn: 'root' })
export class HicapsConnectV2SyncService {
  private readonly _config = inject(NG_PAYMENTS_CONFIG);
  private readonly _featureFlags = inject(FeatureFlagsService);
  private readonly _bridgeCommands = inject(BridgeCommandsService);

  async syncAllBridges(
    practice: IReffable<IPractice>,
    onProgress?: (progress: IHicapsConnectV2SyncProgress) => void
  ): Promise<void> {
    const bridgeDevices = await snapshot(Practice.bridgeDevices$(practice));
    const activeBridgeDevices = bridgeDevices.filter((bridgeDevice) =>
      HicapsConnectV2Routing.isActiveBridgeDevice(bridgeDevice)
    );
    const syncContext = await this._buildSyncContext(practice);
    const total = activeBridgeDevices.length;

    onProgress?.({ completed: 0, total });

    // One-at-a-time: two bridges must never contact the same physical terminal concurrently.
    await resolveSequentially(
      activeBridgeDevices,
      async (bridgeDevice, index) => {
        await this._syncBridgeSafely(bridgeDevice, syncContext);
        onProgress?.({ completed: index + 1, total });
      }
    );
  }

  getDeviceAPI(bridgeDeviceRef: DocumentReference<IBridgeDevice>): HicapsApi {
    const hooks: IHicapsConnectApiHooks = {
      afterProcessCreate: (hicapsProcess) =>
        this._afterProcessCreate(hicapsProcess),
    };
    const adaptor = new HicapsConnectApiAdaptor(
      bridgeDeviceRef,
      this._featureFlags.features,
      hooks
    );
    return new HicapsApi(adaptor);
  }

  getConfig(): IPMSHicapsConnectConfig {
    return this._config.hicapsConnect;
  }

  private async _buildSyncContext(
    practice: IReffable<IPractice>
  ): Promise<IHicapsConnectV2SyncContext> {
    const terminalCol = Practice.hicapsConnectTerminalV2Col(practice);
    const merchantCol = Practice.hicapsConnectMerchantCol(practice);
    const terminals = await Firestore.getDocs(terminalCol);

    // Keep soft-deleted terminal docs visible so deterministic IDs are patched, not re-created.
    return { terminalCol, merchantCol, terminals };
  }

  private async _syncBridgeSafely(
    bridgeDevice: WithRef<IBridgeDevice>,
    syncContext: IHicapsConnectV2SyncContext
  ): Promise<void> {
    try {
      await this._syncBridge(bridgeDevice, syncContext);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        `Unable to sync HICAPS Connect bridge ${bridgeDevice.name} (${bridgeDevice.ref.id})`,
        error
      );
    }
  }

  private async _syncBridge(
    bridgeDevice: WithRef<IBridgeDevice>,
    syncContext: IHicapsConnectV2SyncContext
  ): Promise<void> {
    // Every edge in a pass shares one timestamp so routing's most-recent tiebreak is not skewed by write ordering.
    const lastSeen = Timestamp.now();
    const report = await this._fetchBridgeReport(bridgeDevice);

    await resolveSequentially(report.terminals, async (observed) => {
      await this._upsertMerchants(observed, syncContext);
      await this._upsertTerminal(observed, bridgeDevice, lastSeen, syncContext);
    });

    await this._markStaleEdges(bridgeDevice, report, syncContext);
  }

  private async _fetchBridgeReport(
    bridgeDevice: WithRef<IBridgeDevice>
  ): Promise<IHicapsConnectBridgeReport> {
    const api = this.getDeviceAPI(bridgeDevice.ref);
    const terminalListResponse = await snapshot(api.getTerminalList());

    if (terminalListResponse.result !== HicapsConnectResult.Success) {
      // eslint-disable-next-line no-console
      console.error(
        `Unable to enumerate HICAPS Connect terminals for bridge ${bridgeDevice.name} (${bridgeDevice.ref.id})`,
        terminalListResponse.message
      );
      return { terminals: [], isComplete: false };
    }

    const terminals = await resolveSequentially(
      terminalListResponse.data,
      (serverUrl) => this._fetchObservedTerminal(api, serverUrl)
    );

    return {
      terminals: terminals.filter((terminal) =>
        HicapsConnectTerminal.isValidKey(terminal.terminalId)
      ),
      isComplete: true,
    };
  }

  private async _fetchObservedTerminal(
    api: HicapsApi,
    serverUrl: string
  ): Promise<IHicapsConnectObservedTerminal> {
    const parsedServerUrl = HicapsConnectTerminal.parseServerUrl(serverUrl);
    const request = HicapsConnectHelpers.buildBaseRequest(this.getConfig(), {
      terminalId: serverUrl,
    });
    const merchants = await snapshot(api.sendAllMerchantList(request));
    const observedTerminal = {
      terminalId: parsedServerUrl.terminalId,
      serverUrl: parsedServerUrl.serverUrl,
      isLocal: parsedServerUrl.isLocal,
    };

    // A success envelope only means the bridge replied. HICAPS reports an
    // unreachable terminal as `success` + `FC` + an empty list, which must not
    // be mistaken for "this terminal now carries no merchants".
    const terminalAnswered =
      merchants.result === HicapsConnectResult.Success &&
      HicapsConnectResponseCodes.isTerminalResponseSuccessful(
        merchants.data?.ResponseCode
      );

    if (!terminalAnswered) {
      // eslint-disable-next-line no-console
      console.error(
        `Unable to enumerate HICAPS Connect merchants for terminal ${parsedServerUrl.terminalId} (${serverUrl})`,
        merchants.message
      );
      return { ...observedTerminal, merchants: [], isComplete: false };
    }

    const observedMerchants = uniqBy(
      merchants.data.MerchantListDetails,
      (merchant) => merchant.merchantId
    )
      .filter((merchant) =>
        HicapsConnectTerminal.isValidKey(merchant.merchantId)
      )
      .map((merchant) => ({
        merchantId: merchant.merchantId,
        providerName: merchant.providerName,
      }));

    return {
      ...observedTerminal,
      merchants: observedMerchants,
      isComplete: true,
    };
  }

  private async _upsertMerchants(
    observed: IHicapsConnectObservedTerminal,
    syncContext: IHicapsConnectV2SyncContext
  ): Promise<void> {
    await asyncForEach(observed.merchants, (merchant) =>
      this._upsertMerchant(merchant, syncContext)
    );
  }

  private async _upsertMerchant(
    observed: IHicapsConnectObservedMerchant,
    syncContext: IHicapsConnectV2SyncContext
  ): Promise<void> {
    const existingMerchantRef = Firestore.doc(
      syncContext.merchantCol,
      observed.merchantId
    );
    const existingMerchant = await Firestore.safeGetDoc(existingMerchantRef);

    if (existingMerchant) {
      await Firestore.patchDoc(existingMerchant.ref, {
        providerName: observed.providerName,
      });
      return;
    }

    await Firestore.upsertDoc(
      syncContext.merchantCol,
      {
        merchantId: observed.merchantId,
        providerName: observed.providerName,
        deleted: false,
      },
      observed.merchantId
    );
  }

  private async _upsertTerminal(
    observed: IHicapsConnectObservedTerminal,
    bridgeDevice: WithRef<IBridgeDevice>,
    lastSeen: Timestamp,
    syncContext: IHicapsConnectV2SyncContext
  ): Promise<void> {
    const existingTerminal = await Firestore.safeGetDoc(
      Firestore.doc(syncContext.terminalCol, observed.terminalId)
    );
    const bridges = this._buildBridgeEdgePatch(
      observed,
      bridgeDevice,
      lastSeen
    );
    const merchants = this._buildMerchantEdgePatch(
      observed,
      lastSeen,
      syncContext
    );

    if (existingTerminal) {
      await Firestore.patchDoc(existingTerminal.ref, { bridges, merchants });
      this._refreshTerminalCache(syncContext, {
        ...existingTerminal,
        bridges: { ...existingTerminal.bridges, ...bridges },
        merchants: { ...existingTerminal.merchants, ...merchants },
      });
      return;
    }

    await this._addTerminal(observed, { bridges, merchants }, syncContext);
  }

  private async _addTerminal(
    observed: IHicapsConnectObservedTerminal,
    edges: Pick<IHicapsConnectTerminalV2, 'bridges' | 'merchants'>,
    syncContext: IHicapsConnectV2SyncContext
  ): Promise<void> {
    const newTerminal: IHicapsConnectTerminalV2 = {
      terminalId: observed.terminalId,
      name: `TID:${observed.terminalId}`,
      deleted: false,
      ...edges,
    };
    const terminalRef = await Firestore.upsertDoc(
      syncContext.terminalCol,
      newTerminal,
      observed.terminalId
    );

    syncContext.terminals.push({
      ...newTerminal,
      ref: terminalRef,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
  }

  private _buildBridgeEdgePatch(
    observed: IHicapsConnectObservedTerminal,
    bridgeDevice: WithRef<IBridgeDevice>,
    lastSeen: Timestamp
  ): Record<string, IHicapsConnectBridgeEdge> {
    return {
      [HicapsConnectV2Routing.bridgeEdgeKey(bridgeDevice)]: {
        ref: bridgeDevice.ref,
        serverUrl: observed.serverUrl,
        isLocal: observed.isLocal,
        lastSeen,
        stale: !observed.isComplete,
      },
    };
  }

  private _buildMerchantEdgePatch(
    observed: IHicapsConnectObservedTerminal,
    lastSeen: Timestamp,
    syncContext: IHicapsConnectV2SyncContext
  ): Record<string, IHicapsConnectEdge<IHicapsConnectMerchant>> {
    return Object.fromEntries(
      observed.merchants.map((merchant) => [
        merchant.merchantId,
        {
          ref: Firestore.doc(syncContext.merchantCol, merchant.merchantId),
          lastSeen,
          stale: false,
        },
      ])
    );
  }

  private async _markStaleEdges(
    bridgeDevice: WithRef<IBridgeDevice>,
    report: IHicapsConnectBridgeReport,
    syncContext: IHicapsConnectV2SyncContext
  ): Promise<void> {
    if (!report.isComplete) {
      // Skip stale marking; transient HICAPS errors would falsely break routing.
      return;
    }

    const bridgeEdgeKey = HicapsConnectV2Routing.bridgeEdgeKey(bridgeDevice);
    await this._markStaleBridgeEdges(
      bridgeEdgeKey,
      report.terminals,
      syncContext
    );
    await this._markStaleMerchantEdges(report.terminals, syncContext);
  }

  private async _markStaleBridgeEdges(
    bridgeEdgeKey: string,
    observedTerminals: IHicapsConnectObservedTerminal[],
    syncContext: IHicapsConnectV2SyncContext
  ): Promise<void> {
    const observedTerminalIds = new Set(
      observedTerminals.map((observed) => observed.terminalId)
    );
    const staleTerminals = syncContext.terminals
      .filter(undeletedFilter)
      .filter(
        (terminal) =>
          terminal.bridges[bridgeEdgeKey] &&
          !observedTerminalIds.has(terminal.terminalId)
      );

    await asyncForEach(staleTerminals, async (terminal) => {
      await Firestore.patchDoc(terminal.ref, {
        bridges: { [bridgeEdgeKey]: { stale: true } },
      });
      terminal.bridges[bridgeEdgeKey].stale = true;
    });
  }

  private async _markStaleMerchantEdges(
    observedTerminals: IHicapsConnectObservedTerminal[],
    syncContext: IHicapsConnectV2SyncContext
  ): Promise<void> {
    // Only a fully enumerated terminal is authoritative about its merchants.
    const authoritative = observedTerminals.filter(
      (observed) => observed.isComplete
    );

    await asyncForEach(authoritative, (observed) =>
      this._markStaleMerchantEdgesForTerminal(observed, syncContext)
    );
  }

  private async _markStaleMerchantEdgesForTerminal(
    observed: IHicapsConnectObservedTerminal,
    syncContext: IHicapsConnectV2SyncContext
  ): Promise<void> {
    const terminal = syncContext.terminals.find(
      (item) => item.terminalId === observed.terminalId && undeletedFilter(item)
    );

    if (!terminal) {
      return;
    }

    const observedMerchantIds = new Set(
      observed.merchants.map((merchant) => merchant.merchantId)
    );
    const staleMerchantIds = Object.keys(terminal.merchants).filter(
      (merchantId) => !observedMerchantIds.has(merchantId)
    );

    if (staleMerchantIds.length === 0) {
      return;
    }

    const merchants = Object.fromEntries(
      staleMerchantIds.map((merchantId) => [merchantId, { stale: true }])
    );
    await Firestore.patchDoc(terminal.ref, { merchants });
    staleMerchantIds.forEach((merchantId) => {
      terminal.merchants[merchantId].stale = true;
    });
  }

  private _refreshTerminalCache(
    syncContext: IHicapsConnectV2SyncContext,
    terminal: WithRef<IHicapsConnectTerminalV2>
  ): void {
    const terminalIndex = syncContext.terminals.findIndex(
      (cached) => cached.terminalId === terminal.terminalId
    );

    if (terminalIndex === -1) {
      syncContext.terminals.push(terminal);
      return;
    }

    syncContext.terminals[terminalIndex] = terminal;
  }

  private async _afterProcessCreate<T extends HicapsConnectMethod>(
    hicapsProcess: WithRef<IHicapsConnectProcess<T>>
  ): Promise<void> {
    if (hicapsProcess.status !== HicapsConnectProcessStatus.Pending) {
      return;
    }
    const deviceCommand =
      HicapsConnectProcessHandler.toDeviceCommand(hicapsProcess);
    await this._bridgeCommands.sendCommand(
      deviceCommand.command,
      deviceCommand.deviceRef
    );
    await Firestore.patchDoc(hicapsProcess.ref, {
      status: HicapsConnectProcessStatus.InProgress,
    });
  }
}
